import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';

// Capacitor
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { Animation, StatusBar, Style } from '@capacitor/status-bar';
import { Platform } from '@ionic/angular';

// Nebular
import { NbAuthService } from '@nebular/auth';

// Abstracts
import { NgAnalyticsService, NgInternalLinkService, NgPostTypeProvider, NgPreferenceService } from 'projects/business/src/public-api';

// Interfaces
import { IUser } from 'projects/models/src/public-api';
import { Observable } from 'rxjs';
import { take, takeWhile } from 'rxjs/operators';

// Environment
import { environment as env } from 'src/environments/environment';


interface IMenuItem {
  name: string;
  iconType: 'ionicon' | 'custom';
  icon: string;
  route: string;
};

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

  public pages: IMenuItem[] = [
    { name: 'Dashboard', icon: 'home', iconType: 'ionicon', route: '/dashboard' },
    { name: 'News', icon: 'newspaper', iconType: 'ionicon', route: '/posts' },
    { name: 'Organisation', icon: 'briefcase', iconType: 'ionicon', route: '/organization' },
    { name: 'Mein Urlaubsantrag', icon: 'airplane-sharp', iconType: 'ionicon', route: '/organization/leave-request' },
    { name: 'Mein Mitarbeiterbereich', icon: '/assets/icons/benefits.svg', iconType: 'custom', route: '/benefit' },
    { name: 'Werkstatt', icon: 'build', iconType: 'ionicon', route: '/workshop' },
    { name: 'Profil', icon: 'person', iconType: 'ionicon', route: '/user' },
  ];

  public alive = true;
  public authenticated = false;
  public showIntro = true;

  constructor(
    private platform: Platform,
    private router: Router,
    private authService: NbAuthService,
    private analyticsService: NgAnalyticsService,
    private preferenceService: NgPreferenceService,
    private userService: NgPostTypeProvider<IUser>,
    private zone: NgZone,
    private internalLinkService: NgInternalLinkService) { }

  ngOnInit() {
    this.initializeApp();
    this.getAuthenticationStatus();
  }

  ngOnDestroy() {
    this.alive = false;
  }

  private initializeApp(): void {
    this.platform.ready().then(async () => {
      SplashScreen.hide();
      if (Capacitor.isNativePlatform()) {
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setOverlaysWebView({ overlay: false });
        await StatusBar.setBackgroundColor({ color: '#002855' });
        await StatusBar.show({ animation: Animation.Fade });
        await this.urlListener();
      }
      if (env.production) {
        this.analyticsService.initialize();
        this.routeChange();
      }
    });
  }

  private getAuthenticationStatus() {
    this.authService.onAuthenticationChange()
      .pipe(takeWhile(() => this.alive))
      .subscribe(async (authenticated) => {
        this.authenticated = authenticated;
        if (this.authenticated) {
          this.getUser();
        }
        else {
          this.router.navigate(['auth', 'login'], { replaceUrl: true });
        }
      });
  }

  private routeChange() {
    this.router.events
      .pipe(takeWhile(() => this.alive))
      .subscribe(async (event) => {
        const enabled = await this.preferenceService.get<boolean>('analytics');
        if (enabled === true) {
          if (event instanceof NavigationStart) {
            this.analyticsService.trackEvent('NavigationStart', { url: event.url });
          }
        }
      });
  }

  private getUser(): Promise<IUser> {
    return (this.userService.get(`${env.wp.api}${env.wp.comon}/users/me`) as Observable<IUser>)
      .pipe(take(1))
      .toPromise()
      .then(user => {
        this.analyticsService.setUserId(user.id);
        this.analyticsService.setUserProperty({ name: 'user_name', value: user.name });
        return user;
      });
  }

  private urlListener(): Promise<PluginListenerHandle> {
    return App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(async () => {
        await this.internalLinkService.check(event.url);
      });
    });
  }

}
